import * as Dialog from "@radix-ui/react-dialog";
import { packUnitBreakdown } from "@logiful/domain";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RESHIP_REASONS, type ReshipReason } from "@logiful/domain";
import { BookUser, Minus, PackagePlus, Pencil, Plus, Repeat, Search, Trash2, TriangleAlert, X, Zap } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { api, ApiError, type Address, type AddressBookEntry, type OrderDetail, type ProductItem } from "@/lib/api";
import { countryName, fmtMoney } from "@/lib/format";
import { useTenant } from "./AppShell";
import { Badge, Button, cx, Input } from "./ui";
import { intlLocale, t, msg } from "@/lib/i18n";

export const COUNTRIES = ["DE", "AT", "CH", "NL", "BE", "LU", "FR", "IT", "ES", "PL", "CZ", "DK", "SE", "FI", "NO", "IE", "PT", "GB", "US"];
export const EMPTY: Address = { company: "", firstName: "", lastName: "", street: "", houseNumber: "", addition: "", zip: "", city: "", country: "DE", email: "", phone: "" };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Was eine Position vom Artikel braucht – aus der Artikelsuche oder aus einem bestehenden Auftrag */
type LineProduct = Pick<ProductItem, "id" | "sku" | "name" | "netRetailPrice" | "packUnits"> & { validation: { status: string }; identifiers?: ProductItem["identifiers"] };
export type Line = { product: LineProduct; quantity: number; /** vorgegebene Charge (B2B) */ batch?: string };

/** Bestehenden Auftrag ins Formular übernehmen */
function fromOrder(o: OrderDetail) {
  return {
    addr: { ...EMPTY, ...o.shippingAddress } as Address,
    lines: o.lines.filter((l) => l.productId && !l.addedByRule).map((l) => ({
      product: { id: l.productId!, sku: l.sku, name: l.name, ...(l.unitPriceMinor != null ? { netRetailPrice: { amountMinor: l.unitPriceMinor, currency: o.currency } } : {}), validation: { status: l.blocked ? "invalid" : "valid" } },
      quantity: l.quantity,
    })),
    shippingMethodId: o.shippingMethodId ?? "",
    express: o.priority > 0,
    reference: o.orderNumber,
    note: o.note ?? "",
  };
}

/** Einzeilige Darstellung einer Adresse, z. B. für Adressbuch-Treffer */
export const addressLine = (a: Address) =>
  [a.company, [a.firstName, a.lastName].filter(Boolean).join(" "), [a.street, a.houseNumber].filter(Boolean).join(" "), [a.zip, a.city].filter(Boolean).join(" "), a.country !== "DE" ? countryName(a.country) : null].filter(Boolean).join(" · ");

export function Field({ label, error, className, children }: { label: string; error?: string | undefined; className?: string; children: ReactNode }) {
  return (
    <label className={cx("block min-w-0", className)}>
      <span className="text-[12px] font-medium text-fg-muted">{t(label)}</span>
      <div className="mt-1">{children}</div>
      {error && <span className="text-[11.5px] text-danger">{error}</span>}
    </label>
  );
}

/**
 * Auftrag manuell anlegen (z. B. B2B, Muster, Ersatzlieferung). Läuft danach durch dieselbe Prüfung wie Shop-Aufträge.
 * Empfänger kommen aus dem Adressbuch des Kunden oder können dort gespeichert werden.
 */
export function ManualOrderSheet({ open, onOpenChange, onCreated, edit, reshipOf }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void; edit?: OrderDetail | null; reshipOf?: OrderDetail | null }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  // Nachlieferung: Kopie des Auftrags (alles änderbar), eigene Nummer wird automatisch vergeben
  const init = edit ? fromOrder(edit) : reshipOf ? { ...fromOrder(reshipOf), reference: "", note: "" } : null;
  const [reason, setReason] = useState<ReshipReason | null>(null);
  // Shop-Aufträge: Positionen und Nummer kommen aus Shopify
  const linesLocked = !!edit && edit.channelKind !== "manual";
  const [addr, setAddr] = useState<Address>(init?.addr ?? EMPTY);
  const [bookEntry, setBookEntry] = useState<AddressBookEntry | null>(null);
  const [save, setSave] = useState(!edit && !reshipOf);
  const [saveLabel, setSaveLabel] = useState("");
  const [lines, setLines] = useState<Line[]>(init?.lines ?? []);
  const [shippingMethodId, setShippingMethodId] = useState(init?.shippingMethodId ?? "");
  const [express, setExpress] = useState(init?.express ?? false);
  const [reference, setReference] = useState(init?.reference ?? "");
  const [note, setNote] = useState(init?.note ?? "");
  const [tried, setTried] = useState(false);

  const reset = () => {
    setAddr(EMPTY); setBookEntry(null); setSave(true); setSaveLabel(""); setLines([]);
    setShippingMethodId(""); setExpress(false); setReference(""); setNote(""); setTried(false);
  };

  const set = (k: keyof Address, v: string) => {
    setAddr((a) => ({ ...a, [k]: v }));
    // Geänderte Adressbuch-Adresse ist eine neue Adresse – Verknüpfung lösen, Speichern anbieten
    if (bookEntry) { setBookEntry(null); setSave(false); }
  };
  const pick = (e: AddressBookEntry) => {
    setAddr({ ...EMPTY, ...e.address });
    setBookEntry(e);
  };

  const methods = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), enabled: open, retry: false, staleTime: 5 * 60_000 });
  const activeMethods = methods.data?.warehouses.find((w) => w.active)?.shippingMethods ?? [];

  const errors = {
    lastName: !addr.lastName?.trim() ? "Nachname oder Ansprechpartner fehlt" : undefined,
    street: !addr.street?.trim() ? "Straße fehlt" : undefined,
    zip: !addr.zip?.trim() ? "PLZ fehlt" : addr.country === "DE" && !/^\d{5}$/.test(addr.zip.trim()) ? "Deutsche PLZ hat 5 Ziffern" : undefined,
    city: !addr.city?.trim() ? "Ort fehlt" : undefined,
    email: addr.email && !EMAIL.test(addr.email.trim()) ? "E-Mail ungültig" : undefined,
    lines: lines.length === 0 ? "Mindestens einen Artikel hinzufügen" : undefined,
    reason: reshipOf && !reason ? "Bitte einen Grund wählen" : undefined,
  };
  const valid = Object.values(errors).every((e) => !e);
  const err = (k: keyof typeof errors) => (tried ? errors[k] : undefined);

  const units = lines.reduce((s, l) => s + l.quantity, 0);
  const total = lines.every((l) => l.product.netRetailPrice) ? lines.reduce((s, l) => s + l.product.netRetailPrice!.amountMinor * l.quantity, 0) : null;
  const blocked = lines.filter((l) => l.product.validation.status === "invalid");

  const update = useMutation({
    mutationFn: () => {
      const clean = Object.fromEntries(Object.entries(addr).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]).filter(([, v]) => v !== "")) as Address;
      return api.orders.update(tenantId, edit!.id, {
        address: clean,
        shippingMethodId: shippingMethodId || null,
        note: note.trim() || null,
        express,
        ...(linesLocked ? {} : { lines: lines.map((l) => ({ productId: l.product.id, quantity: l.quantity })), ...(reference.trim() && reference.trim() !== edit!.orderNumber ? { reference: reference.trim() } : {}) }),
      });
    },
    onSuccess: () => {
      toast.success(`Auftrag ${edit!.orderNumber} gespeichert`, { description: msg("Wird neu geprüft und – wenn alles passt – ans Lager übergeben.") });
      if (save && !bookEntry) void api.addressBook.create(tenantId, { address: addr, ...(saveLabel.trim() ? { label: saveLabel.trim() } : {}) }).then(() => qc.invalidateQueries({ queryKey: ["address-book", tenantId] }));
      void qc.invalidateQueries({ queryKey: ["order", tenantId, edit!.id] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Speichern fehlgeschlagen") : "Speichern fehlgeschlagen"),
  });

  const create = useMutation({
    mutationFn: () => {
      const clean = Object.fromEntries(Object.entries(addr).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]).filter(([, v]) => v !== "")) as Address;
      return api.orders.create(tenantId, {
        address: clean,
        lines: lines.map((l) => ({ productId: l.product.id, quantity: l.quantity })),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(shippingMethodId ? { shippingMethodId } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        express,
        ...(bookEntry ? { addressBookId: bookEntry.id } : { saveToAddressBook: save, ...(save && saveLabel.trim() ? { addressBookLabel: saveLabel.trim() } : {}) }),
        ...(reshipOf && reason ? { reshipOf: reshipOf.id, reshipReason: reason } : {}),
      });
    },
    onSuccess: (r) => {
      if (reshipOf) void qc.invalidateQueries({ queryKey: ["order", tenantId, reshipOf.id] });
      toast.success(reshipOf ? `Nachlieferung ${r.orderNumber} angelegt` : `Auftrag ${r.orderNumber} angelegt`, { description: [blocked.length ? "Wird wegen Stammdaten zurückgehalten – bitte Artikel korrigieren." : "Wird geprüft und ans Lager übergeben.", r.addressBookId ? "Empfänger im Adressbuch gespeichert." : ""].filter(Boolean).join(" ") });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
      void qc.invalidateQueries({ queryKey: ["orders", tenantId] });
      void qc.invalidateQueries({ queryKey: ["address-book", tenantId] });
      reset();
      onOpenChange(false);
      onCreated(r.id);
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Auftrag konnte nicht angelegt werden") : "Auftrag konnte nicht angelegt werden"),
  });

  const submit = () => {
    setTried(true);
    if (valid) (edit ? update : create).mutate();
    // Zum ersten fehlenden Feld springen
    else requestAnimationFrame(() => document.querySelector("[data-sheet-body] .text-danger")?.scrollIntoView({ block: "center", behavior: "smooth" }));
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content onOpenAutoFocus={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { if ((document.activeElement as HTMLElement | null)?.closest("[data-picker=open]")) e.preventDefault(); /* Esc schließt erst die offene Trefferliste */ }} className="fixed z-50 right-0 top-0 h-full w-[min(680px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2">{edit ? <><Pencil className="size-4 text-fg-subtle" /> {t("Auftrag")} {edit.orderNumber} {t("bearbeiten")}</> : reshipOf ? <><Repeat className="size-4 text-fg-subtle" /> {t("Nachlieferung zu")} {reshipOf.orderNumber}</> : <><PackagePlus className="size-4 text-fg-subtle" /> {t("Auftrag anlegen")}</>}</Dialog.Title>
              <Dialog.Description className="text-[13px] text-fg-muted mt-0.5">
                {edit
                  ? linesLocked
                    ? t("Empfänger, Versandart und Notiz lassen sich ändern. Die Positionen kommen aus Shopify.")
                    : t("Solange der Auftrag nicht im Lager ist, ist alles änderbar. Danach wird er neu geprüft.")
                  : reshipOf
                    ? t("Kopie des Auftrags – alles änderbar: nur fehlende Positionen, neue Adresse oder andere Versandart. Wird als eigener Auftrag geprüft und ans Lager übergeben; im Shop passiert nichts.")
                    : t("Z. B. für B2B, Muster oder Ersatzlieferungen. Der Auftrag wird wie ein Shop-Auftrag geprüft und ans Lager übergeben.")}
              </Dialog.Description>
            </div>
            <Dialog.Close className="size-8 -mr-2 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>

          <div data-sheet-body className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
            {reshipOf && (
              <section>
                <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Grund der Nachlieferung")}</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(Object.entries(RESHIP_REASONS) as [ReshipReason, string][]).map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setReason(k)} aria-pressed={reason === k} className={cx("h-8 px-3 rounded-lg border text-[12.5px] font-medium transition-colors", reason === k ? "border-ink bg-ink text-ink-fg" : "border-border hover:border-border-strong")}>{t(label)}</button>
                  ))}
                </div>
                {err("reason") && <p className="mt-1.5 text-[11.5px] text-danger">{err("reason")}</p>}
                <p className="mt-2 text-[12px] text-fg-subtle">{t("Steht fürs Lager in der Notiz („Nachlieferung zu")} {reshipOf.orderNumber} {t("…“) und an beiden Aufträgen im Verlauf.")}</p>
              </section>
            )}
            {/* Empfänger */}
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Empfänger")}</h3>
              <AddressBookPicker open={open} selected={bookEntry} onPick={pick} onClear={() => { setBookEntry(null); setAddr(EMPTY); setSave(true); }} />
              <div className="mt-3 grid grid-cols-6 gap-x-3 gap-y-2.5">
                <Field label={t("Firma (optional)")} className="col-span-6"><Input value={addr.company ?? ""} onChange={(e) => set("company", e.target.value)} placeholder={t("z. B. Nordlicht Store GmbH")} /></Field>
                <Field label={t("Vorname")} className="col-span-6 sm:col-span-3"><Input value={addr.firstName ?? ""} onChange={(e) => set("firstName", e.target.value)} autoComplete="off" /></Field>
                <Field label={t("Nachname / Ansprechpartner")} error={err("lastName")} className="col-span-6 sm:col-span-3"><Input value={addr.lastName} onChange={(e) => set("lastName", e.target.value)} invalid={!!err("lastName")} autoComplete="off" /></Field>
                <Field label={t("Straße")} error={err("street")} className="col-span-4"><Input value={addr.street} onChange={(e) => set("street", e.target.value)} invalid={!!err("street")} autoComplete="off" /></Field>
                <Field label={t("Nr.")} className="col-span-2"><Input value={addr.houseNumber ?? ""} onChange={(e) => set("houseNumber", e.target.value)} /></Field>
                <Field label={t("Adresszusatz (optional)")} className="col-span-6"><Input value={addr.addition ?? ""} onChange={(e) => set("addition", e.target.value)} placeholder={t("z. B. Hinterhaus, Warenannahme Tor 3")} /></Field>
                <Field label={t("PLZ")} error={err("zip")} className="col-span-2"><Input value={addr.zip} onChange={(e) => set("zip", e.target.value)} invalid={!!err("zip")} inputMode={addr.country === "DE" ? "numeric" : "text"} /></Field>
                <Field label={t("Ort")} error={err("city")} className="col-span-4 sm:col-span-2"><Input value={addr.city} onChange={(e) => set("city", e.target.value)} invalid={!!err("city")} /></Field>
                <Field label={t("Land")} className="col-span-6 sm:col-span-2">
                  <select value={addr.country} onChange={(e) => set("country", e.target.value)} className="h-8 w-full rounded-lg border border-border bg-surface px-2 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-fg/10">
                    {COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                  </select>
                </Field>
                <Field label={t("E-Mail (für Versandbenachrichtigung)")} error={err("email")} className="col-span-6 sm:col-span-3"><Input type="email" value={addr.email ?? ""} onChange={(e) => set("email", e.target.value)} invalid={!!err("email")} /></Field>
                <Field label={t("Telefon (optional)")} className="col-span-6 sm:col-span-3"><Input type="tel" value={addr.phone ?? ""} onChange={(e) => set("phone", e.target.value)} /></Field>
              </div>
              {!bookEntry && (
                <div className="mt-3 rounded-xl border border-border bg-surface-2/40 p-3">
                  <button type="button" role="switch" aria-checked={save} onClick={() => setSave(!save)} className="flex items-center gap-2.5 text-left">
                    <span className={cx("w-8 h-[18px] shrink-0 rounded-full p-0.5 transition-colors", save ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", save && "translate-x-3.5")} /></span>
                    <span className="text-[13px]"><span className="font-medium">{t("Empfänger im Adressbuch speichern")}</span> <span className="text-fg-subtle">{t("· nur für diesen Kunden sichtbar")}</span></span>
                  </button>
                  {save && <Input value={saveLabel} onChange={(e) => setSaveLabel(e.target.value)} placeholder={t("Name im Adressbuch (optional), z. B. Filiale Köln")} className="mt-2.5" />}
                </div>
              )}
            </section>

            {/* Positionen */}
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Positionen")}</h3>
              {!linesLocked && <ProductPicker open={open} exclude={lines.map((l) => l.product.id)} onPick={(p) => setLines((ls) => [...ls, { product: p, quantity: 1 }])} />}
              {err("lines") && <p className="mt-1.5 text-[11.5px] text-danger">{err("lines")}</p>}
              {lines.length > 0 && (
                <ul className="mt-3 rounded-xl border border-border divide-y divide-border">
                  {lines.map((l, i) => (
                    <LineRow
                      key={l.product.id}
                      line={l}
                      locked={linesLocked}
                      onQty={(q) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, quantity: q } : x)))}
                      onRemove={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                    />
                  ))}
                </ul>
              )}
              {blocked.length > 0 && (
                <p className="mt-2 text-[12.5px] text-warning flex items-start gap-1.5"><TriangleAlert className="size-3.5 mt-0.5 shrink-0" /> {blocked.map((l) => l.product.sku).join(", ")}{t(": Stammdaten unvollständig – der Auftrag")} {edit ? t("bleibt") : t("wird angelegt, aber")} {t("zurückgehalten, bis die Artikel korrigiert sind.")}</p>
              )}
            </section>

            {/* Versand & Details */}
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Versand & Details")}</h3>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2.5">
                <Field label={t("Versandart")} className="col-span-2 sm:col-span-1">
                  <select value={shippingMethodId} onChange={(e) => setShippingMethodId(e.target.value)} className="h-8 w-full rounded-lg border border-border bg-surface px-2 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-fg/10">
                    <option value="">{t("Automatisch (nach Regeln)")}</option>
                    {activeMethods.map((m) => <option key={m.id} value={m.id}>{m.name}{m.carrier && !m.name.includes(m.carrier) ? ` · ${m.carrier}` : ""}{m.cutoffTime ? t(" · bis {v} Uhr", { v: m.cutoffTime.slice(0, 5) }) : ""}</option>)}
                  </select>
                  {methods.isError && <span className="text-[11.5px] text-fg-subtle">{t("Versandarten nicht verfügbar – das Lager wählt automatisch.")}</span>}
                </Field>
                {!linesLocked && <Field label={edit ? t("Auftragsnummer") : t("Eigene Auftragsnummer (optional)")} className="col-span-2 sm:col-span-1"><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder={reshipOf ? t("sonst automatisch {orderNumber}-NL", { orderNumber: reshipOf.orderNumber }) : t("sonst automatisch M-2026-…")} className="font-mono" /></Field>}
                <Field label={t("Notiz fürs Lager (optional)")} className="col-span-2">
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder={t("z. B. Bitte Lieferschein ohne Preise beilegen")} className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-[13px] placeholder:text-fg-subtle focus:outline-none focus:border-fg-muted focus:ring-2 focus:ring-fg/10" />
                </Field>
                <button type="button" role="switch" aria-checked={express} onClick={() => setExpress(!express)} className="col-span-2 flex items-center gap-2.5 text-left">
                  <span className={cx("w-8 h-[18px] shrink-0 rounded-full p-0.5 transition-colors", express ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", express && "translate-x-3.5")} /></span>
                  <span className="text-[13px] flex items-center gap-1.5"><Zap className="size-3.5 text-fg-subtle" /> <span className="font-medium">{t("Bevorzugt kommissionieren")}</span> <span className="text-fg-subtle">{t("· wird im Lager vorgezogen")}</span></span>
                </button>
              </div>
            </section>
          </div>

          <div className="px-6 py-3.5 border-t border-border flex items-center gap-3 bg-surface">
            <div className="min-w-0 flex-1 text-[12.5px] text-fg-muted">
              {lines.length ? <><b className="text-fg">{lines.length}</b> {lines.length === 1 ? t("Position") : t("Positionen")} · {units} {t("Stück")}{total != null && <> · <span className="tabular-nums">{fmtMoney(total, "EUR")}</span></>}</> : t("Noch keine Artikel")}
              {tried && !valid && <span className="block text-danger">{t("Bitte die markierten Felder ausfüllen.")}</span>}
            </div>
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" loading={create.isPending || update.isPending} onClick={submit}>{edit ? t("Änderungen speichern") : reshipOf ? t("Nachlieferung anlegen") : t("Auftrag anlegen")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Suche im Adressbuch des Kunden; ohne Eingabe die meistgenutzten Empfänger */
function AddressBookPicker({ open, selected, onPick, onClear }: { open: boolean; selected: AddressBookEntry | null; onPick: (e: AddressBookEntry) => void; onClear: () => void }) {
  const { tenantId } = useTenant();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 150);
    return () => clearTimeout(t);
  }, [q]);
  const list = useQuery({ queryKey: ["address-book", tenantId, debounced], queryFn: () => api.addressBook.list(tenantId, debounced), enabled: open, placeholderData: keepPreviousData });
  const entries = list.data ?? [];

  if (selected)
    return (
      <div className="mt-2 flex items-center gap-2 rounded-xl border border-ink/20 bg-surface-2/50 px-3 py-2 text-[13px]">
        <BookUser className="size-4 text-fg-subtle shrink-0" />
        <span className="min-w-0 flex-1 truncate"><span className="font-medium">{selected.label ?? t("Aus dem Adressbuch")}</span> <span className="text-fg-muted">· {addressLine(selected.address)}</span></span>
        <button onClick={onClear} className="text-[12px] font-medium text-fg-muted hover:text-fg shrink-0">{t("Andere Adresse")}</button>
      </div>
    );

  if (!list.isLoading && entries.length === 0 && !debounced) return null; // leeres Adressbuch: kein Suchfeld

  return (
    <div className="relative mt-2" data-picker={focus ? "open" : "closed"}>
      <Input value={q} onChange={(e) => { setQ(e.target.value); setFocus(true); }} onKeyDown={(e) => { if (e.key === "Escape") setFocus(false); }} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)} leading={<BookUser className="size-4" />} placeholder={t("Aus Adressbuch wählen – Name, Firma, Ort …")} />
      {focus && (
        <ul className="absolute z-10 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-border bg-surface shadow-xl divide-y divide-border animate-fade-in">
          {entries.slice(0, 8).map((e) => (
            <li key={e.id}>
              <button onMouseDown={(ev) => ev.preventDefault()} onClick={() => { onPick(e); setQ(""); setFocus(false); }} className="w-full text-left px-3 py-2 hover:bg-surface-2/60">
                <div className="text-[13px] font-medium truncate">{e.label ?? (e.address.company || [e.address.firstName, e.address.lastName].filter(Boolean).join(" "))}</div>
                <div className="text-[12px] text-fg-muted truncate">{addressLine(e.address)}{e.useCount > 1 && <span className="text-fg-subtle"> · {e.useCount}{t("× genutzt")}</span>}</div>
              </button>
            </li>
          ))}
          {entries.length === 0 && <li className="px-3 py-3 text-[13px] text-fg-muted">{t("Kein Treffer im Adressbuch.")}</li>}
        </ul>
      )}
    </div>
  );
}

/** Artikelsuche mit verfügbarem Bestand */
export function ProductPicker({ open, exclude, onPick }: { open: boolean; exclude: string[]; onPick: (p: ProductItem) => void }) {
  const { tenantId } = useTenant();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [focus, setFocus] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 150);
    return () => clearTimeout(t);
  }, [q]);
  const products = useQuery({ queryKey: ["products", tenantId, { search: debounced, status: "active", pageSize: 8, picker: true }], queryFn: () => api.products.list(tenantId, { search: debounced || undefined, status: "active", pageSize: 8 }), enabled: open && focus, placeholderData: keepPreviousData });
  const stock = useQuery({ queryKey: ["stock", tenantId, ""], queryFn: () => api.stock.list(tenantId), enabled: open, staleTime: 60_000 });
  const available = useMemo(() => new Map((stock.data ?? []).map((s) => [s.productId, s.available])), [stock.data]);
  const items = (products.data?.items ?? []).filter((p) => !exclude.includes(p.id));

  const choose = (p: ProductItem) => {
    onPick(p);
    setQ("");
    setActive(0);
    setFocus(false); // Liste schließen; Tippen öffnet sie wieder (schnelles Erfassen mehrerer Artikel)
  };

  return (
    <div className="relative mt-2" data-picker={focus ? "open" : "closed"}>
      <Input
        ref={ref}
        value={q}
        onChange={(e) => { setQ(e.target.value); setActive(0); setFocus(true); }}
        onClick={() => setFocus(true)}
        onFocus={() => setFocus(true)}
        onBlur={() => setTimeout(() => setFocus(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
          if (e.key === "Enter" && items[active] && focus) { e.preventDefault(); choose(items[active]!); }
          if (e.key === "Escape") setFocus(false);
        }}
        leading={<Search className="size-4" />}
        placeholder={t("Artikel hinzufügen – SKU, Name oder EAN (Enter übernimmt)")}
      />
      {focus && (
        <ul className="absolute z-10 mt-1 w-full max-h-80 overflow-y-auto rounded-xl border border-border bg-surface shadow-xl divide-y divide-border animate-fade-in">
          {items.map((p, i) => {
            const av = available.get(p.id);
            return (
              <li key={p.id}>
                <button onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => choose(p)} className={cx("w-full text-left px-3 py-2 flex items-center gap-3", i === active && "bg-surface-2/70")}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium truncate">{p.name}</div>
                    <div className="text-[12px] text-fg-muted font-mono truncate">{p.sku}{p.identifiers.gtin && ` · ${p.identifiers.gtin}`}</div>
                  </div>
                  {p.validation.status === "invalid" && <Badge tone="warning">{t("Stammdaten")}</Badge>}
                  <span className={cx("text-[12px] tabular-nums whitespace-nowrap", av === 0 ? "text-danger" : "text-fg-muted")}>{av == null ? "–" : t("{av} verfügbar", { av })}</span>
                </button>
              </li>
            );
          })}
          {products.isFetching && items.length === 0 && <li className="px-3 py-3 text-[13px] text-fg-muted">{t("Suche …")}</li>}
          {!products.isFetching && items.length === 0 && <li className="px-3 py-3 text-[13px] text-fg-muted">{debounced ? t("Kein aktiver Artikel für „{debounced}“.", { debounced }) : t("Keine weiteren Artikel.")}</li>}
        </ul>
      )}
    </div>
  );
}

/** Charge aus dem Logiful-Bestand vorgeben (frühestes MHD zuerst) – geht als Notiz an die Lager-Position */
function BatchSelect({ productId, quantity, value, onChange }: { productId: string; quantity: number; value?: string; onChange: (b: string | undefined) => void }) {
  const { tenantId } = useTenant();
  const lots = useQuery({ queryKey: ["lots", tenantId, productId], queryFn: () => api.b2b.lots(tenantId, productId), staleTime: 60_000 });
  if (lots.isLoading || !lots.data?.length) return null;
  const sel = lots.data.find((l) => l.batch === value);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px]">
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)} className="h-7 rounded-md border border-border bg-surface px-1.5 text-[12px]" aria-label={t("Charge vorgeben")}>
        <option value="">{t("Charge: Lager wählt (FIFO/MHD)")}</option>
        {lots.data.map((l) => <option key={l.batch} value={l.batch}>{t("Charge")} {l.batch}{l.bestBefore ? t(" · MHD {v}", { v: new Date(l.bestBefore).toLocaleDateString(intlLocale()) }) : ""} · {l.available} {t("frei")}</option>)}
      </select>
      {sel && sel.available < quantity && <span className="text-warning">{t("nur")} {sel.available} {t("in dieser Charge")}</span>}
    </div>
  );
}

export function LineRow({ line, onQty, onRemove, locked, onBatch }: { line: Line; onQty: (q: number) => void; onRemove: () => void; locked?: boolean; /** Chargen-Vorgabe anbieten (B2B) */ onBatch?: (b: string | undefined) => void }) {
  const { tenantId } = useTenant();
  const stock = useQuery({ queryKey: ["stock", tenantId, ""], queryFn: () => api.stock.list(tenantId), staleTime: 60_000 });
  const av = stock.data?.find((s) => s.productId === line.product.id)?.available;
  const short = av != null && line.quantity > av;
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium truncate">{line.product.name}</div>
        <div className="text-[12px] text-fg-muted"><span className="font-mono">{line.product.sku}</span>{av != null && <span className={cx(short && "text-warning")}> · {av} {t("verfügbar")}{short && t(" – mehr als im Lager")}</span>}</div>
        {onBatch && packUnitBreakdown(line.quantity, line.product.packUnits) && <div className="text-[11.5px] text-fg-muted">= {packUnitBreakdown(line.quantity, line.product.packUnits)}</div>}
        {onBatch && <BatchSelect productId={line.product.id} quantity={line.quantity} value={line.batch} onChange={onBatch} />}
      </div>
      {locked ? <span className="text-[13px] tabular-nums text-fg-muted">{line.quantity}×</span> : <div className="flex items-center rounded-lg border border-border">
        <button onClick={() => onQty(Math.max(1, line.quantity - 1))} disabled={line.quantity <= 1} className="size-7 grid place-items-center text-fg-muted hover:text-fg disabled:opacity-40" aria-label={t("Weniger")}><Minus className="size-3.5" /></button>
        <input
          value={line.quantity}
          onChange={(e) => { const n = Number.parseInt(e.target.value.replace(/\D/g, ""), 10); onQty(Number.isFinite(n) && n > 0 ? Math.min(n, 10_000) : 1); }}
          inputMode="numeric"
          aria-label={t("Menge {sku}", { sku: line.product.sku })}
          className="w-12 h-7 text-center tabular-nums text-[13px] bg-transparent outline-none border-x border-border"
        />
        <button onClick={() => onQty(Math.min(10_000, line.quantity + 1))} className="size-7 grid place-items-center text-fg-muted hover:text-fg" aria-label={t("Mehr")}><Plus className="size-3.5" /></button>
      </div>}
      <span className="w-20 text-right text-[12.5px] tabular-nums text-fg-muted">{line.product.netRetailPrice ? fmtMoney(line.product.netRetailPrice.amountMinor * line.quantity, "EUR") : "—"}</span>
      {!locked && <button onClick={onRemove} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-danger" aria-label={t("{sku} entfernen", { sku: line.product.sku })}><Trash2 className="size-3.5" /></button>}
    </li>
  );
}
